<script>
import { cloneDeep } from 'lodash-es';
import ValidatorMixin from '../../mixins/validatorMixin';
import TwWysiwyg from '../TwWysiwyg';
import TwCopyAndTranslateModal from '../TwCopyAndTranslateModal';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'itinerary-seo-teaser-list',
  mixins: [ValidatorMixin],
  components: { TwCopyAndTranslateModal, TwWysiwyg },
  props: {
    itineraryId: {
      type: Number,
      required: true,
    },
    seoLoading: {
      type: Boolean,
      default: false,
    },
    seoTeaserItems: {
      type: Array,
      default: () => [],
    },
    seoTeaserError: {
      type: Boolean,
      default: false,
    },
    selectedDomain: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      localSeoTeaserItems: [],
      headers: [
        { text: this.$t('itinerary.edit.seo.table.no'), sortable: false },
        { text: this.$t('itinerary.edit.seo.table.description'), sortable: false },
        { text: this.$t('itinerary.edit.seo.table.actions'), sortable: false, align: 'center' },
      ],
      valid: true,
      copyAndTranslateModalOpen: false,
    };
  },
  created() {
    this.localSeoTeaserItems = cloneDeep(this.seoTeaserItems);
    this.getItineraryDomains(this.itineraryId);
  },
  computed: {
    ...mapState({
      itineraryDomains: state => state.itinerary.domains.items,
    }),
    getSeoCopyValues() {
      const values = [];
      this.seoTeaserItems.forEach((seo, index) => {
        const copySeoDomainMapping = [];
        this.itineraryDomains.forEach((itineraryDomain) => {
          copySeoDomainMapping[itineraryDomain.domainId] = [
            {
              column: 'itinerary_teasers_id',
              value: seo.id,
            },
          ];
        });
        values.push({
          globalModelId: seo.id,
          globalModelColumnName: 'itinerary_teasers_id',
          selectKeyOverrides: ['itinerary_teasers_id', 'domain_id'],
          tableName: 'itinerary_domain_teasers',
          sourceDomainId: null,
          selectedFromDomain: null,
          fromLanguageIsoOverride: null,
          sourceIsoOverride: null,
          toDomains: [],
          shouldTranslate: false,
          shouldCopySeo: false,
          fieldNamesAndValuesToCopyAndTranslate: [
            { description: seo.description },
            { itinerary_teasers_id: seo.id },
            { domain_id: null },
          ],
          title: `Teaser: ${index + 1}`,
        });
      });
      return values;
    },
  },
  methods: {
    ...mapActions({
      getItineraryDomains: 'itinerary/getItineraryDomains',
    }),
    save() {
      this.$emit('save-teasers', this.localSeoTeaserItems);
    },
    updateDomainText(event, domainId) {
      if (domainId) {
        this.localSeoTeaserItems[0].domains[domainId] = event;
      } else {
        this.localSeoTeaserItems[0].description = event;
      }
    },
  },
  watch: {
    seoTeaserItems() {
      this.localSeoTeaserItems = cloneDeep(this.seoTeaserItems);
    },
  },
};
</script>

<template>
  <v-container class="pa-0 fluid">
    <v-card-title class="pb-0">
      <div class="headline">{{ $t('itinerary.edit.seo.table.title') }}</div>
    </v-card-title>

    <v-layout row wrap>
      <v-flex class="my-4">
        <v-form v-model="valid">
          <v-data-table
            :items="localSeoTeaserItems"
            :headers="headers"
            :loading="seoLoading"
            hide-actions
            class="elevation-1"
          >
            <template v-slot:items="props">
              <tr>
                <td>
                  <template v-if="props.index == 0">
                    {{ $t('itinerary.edit.seo.table.iterator', [props.index + 1]) }} (main page)
                  </template>
                  <template v-else>
                    {{ $t('itinerary.edit.seo.table.iterator', [props.index]) }} (tag line)
                  </template>
                </td>
                <td v-if="props.index == 0">
                  <tw-wysiwyg
                    v-model="props.item.description"
                    :label="$t('itinerary.edit.seo.table.description')"
                    class="my-3"
                    counter="8000"
                    @change="updateDomainText($event, null)"
                  />
                  <tw-wysiwyg
                    v-if="selectedDomain"
                    :value="props.item.domains[''+selectedDomain.domainId]"
                    :label="$t('itinerary.edit.seo.table.localDescription')"
                    class="my-3"
                    counter="8000"
                    @change="updateDomainText($event, selectedDomain.domainId)"
                  />
                </td>
                <td v-else>
                  <v-text-field
                    v-model="props.item.description"
                    :label="$t('itinerary.edit.seo.table.description')"
                    class="my-3"
                  />
                  <v-text-field
                    v-if="selectedDomain"
                    v-model="props.item.domains[''+selectedDomain.domainId]"
                    :label="$t('itinerary.edit.seo.table.localDescription')"
                    hide-details
                    class="my-3"
                  />
                </td>
                <td class="text-xs-center">
                  <v-btn
                    fab
                    small
                    depressed
                    color="error"
                    @click="localSeoTeaserItems.splice(props.index, 1)"
                  >
                    <v-icon>delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
          <v-btn
            color="primary success"
            class="mt-4"
            :loading="seoLoading"
            @click="copyAndTranslateModalOpen = !copyAndTranslateModalOpen"
          >
            Copy/translate
          </v-btn>
          <tw-copy-and-translate-modal
            v-if="copyAndTranslateModalOpen"
            :elements-to-handle="getSeoCopyValues"
            :domains="itineraryDomains.map(domain => domain.domain)"
          />
          <v-btn
            :loading="seoLoading"
            color="success"
            class="right mr-4 mt-4"
            @click="localSeoTeaserItems.push( { id:null, itineraryId, description:'', domains:{} })"
          >
            {{ $t('buttons.add') }}
            <v-icon class="ml-1">add</v-icon>
          </v-btn>
          <v-btn
            :loading="seoLoading"
            color="primary"
            class="right mr-4 mt-4"
            @click="save"
          >
            {{ $t('buttons.save') }}
            <v-icon class="ml-1">save</v-icon>
          </v-btn>
        </v-form>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style lang="scss" scoped>
  @import '../../assets/scss/variables.scss';

  .expanded-row {
    position: relative;
    display: table-row;
    height: $expanded-row-height;
    width: 100%;
    overflow: hidden;
  }

  .expanded-row-input {
    margin-top: $margin-lg;
    margin-left: $margin-lg;
    width: $expanded-row-input-width;
  }

  .expanded-row-data {
    position: absolute;
    width: 100%;
    height: $expanded-row-height;
    overflow: auto;
  }

  .expanded-row-form {
    width: 50%;
    float: right;
  }
</style>



// WEBPACK FOOTER //
// src/components/itinerary/ItinerarySEOTeaserList.vue